import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as api from '../services/quantumBattleshipApi';
import { fleetPayload, normalizeSettings } from './model.mjs';
import { initialSessionId, readStorage, writeStorage } from './storage.mjs';

const MatchContext = createContext(null);
export function MatchProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    return normalizeSettings(readStorage('qb_settings_v2'));
  });
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(initialSessionId()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const gameRef = useRef(null);
  const busyRef = useRef(false);
  const generation = useRef(0);
  const accept = useCallback(next => {
    gameRef.current = next;
    setGame(next);
    setSessionId(next.game_id);
    writeStorage('qb_game_v2', { id: next.game_id });
    setNeedsRefresh(false);
  }, []);
  useEffect(() => {
    if (!sessionId || gameRef.current?.game_id === sessionId) { setLoading(false); return; }
    let cancelled = false;
    const revision = generation.current;
    setLoading(true);
    api.getGameState(sessionId).then(next => {
      if (!cancelled && generation.current === revision) { accept(next); setError(''); }
    }).catch(problem => {
      if (!cancelled) setError(problem.status === 404 ? 'This saved match is no longer available. The server may have restarted.' : problem.message);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, accept]);
  const begin = useCallback(next => {
    generation.current += 1;
    const normalized = normalizeSettings(next);
    setSettings(normalized);
    writeStorage('qb_settings_v2', normalized);
    for (const key of ['qb_game_v2', 'qb_last_game_session', 'qb_placement_v2']) writeStorage(key, null);
    gameRef.current = null;
    setGame(null);
    setSessionId(null);
    setLoading(false);
    setError('');
    setNeedsRefresh(false);
  }, []);
  const refresh = useCallback(async () => {
    if (busyRef.current || !sessionId) return null;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try { const next = await api.getGameState(sessionId); accept(next); return next; }
    catch (problem) { setError(problem.message); setNeedsRefresh(true); return null; }
    finally { busyRef.current = false; setBusy(false); }
  }, [accept, sessionId]);
  // A lost response may hide a successful move. Recover with GET, never a second shot.
  const mutate = useCallback(async operation => {
    if (busyRef.current) return null;
    busyRef.current = true;
    setBusy(true);
    setError('');
    const previous = gameRef.current;
    try { const next = await operation(previous); accept(next); return next; }
    catch (problem) {
      const id = gameRef.current?.game_id;
      if (id) {
        try {
          const recovered = await api.getGameState(id);
          accept(recovered);
          const changed = recovered.status !== previous?.status || recovered.attack_history?.length !== previous?.attack_history?.length || recovered.player_entanglement_used !== previous?.player_entanglement_used;
          if (changed) { setError('The response was interrupted. The match has been refreshed.'); return recovered; }
        } catch { setNeedsRefresh(true); }
      }
      setError(problem.message);
      return null;
    } finally { busyRef.current = false; setBusy(false); }
  }, [accept]);
  const deploy = useCallback(fleet => mutate(async current => {
    let id = current?.status === 'setup' ? current.game_id : null;
    if (!id) {
      const created = await api.createBackendGame(settings);
      id = created.game_id;
      gameRef.current = { ...created, attack_history: [], player_entanglement_used: false };
      setSessionId(id);
      writeStorage('qb_game_v2', { id });
    }
    return api.placeFleet(id, fleetPayload(fleet));
  }), [settings, mutate]);
  const attack = useCallback((coordinate, attackType) => mutate(current => {
    if (!current || current.status !== 'active' || needsRefresh) throw new Error('Refresh the match before attacking.');
    return api.executePlayerTurn(current.game_id, { coordinate, attackType, expectedTurn: current.turn_number }).then(response => response.game);
  }), [mutate, needsRefresh]);
  const entangle = useCallback(() => mutate(current => {
    if (!current || current.status !== 'active' || needsRefresh) throw new Error('Refresh the match before creating a link.');
    return api.usePlayerEntanglement(current.game_id).then(response => response.game);
  }), [mutate, needsRefresh]);
  return <MatchContext.Provider value={{ game, settings, sessionId, loading, busy, error, needsRefresh, begin, refresh, deploy, attack, entangle }}>{children}</MatchContext.Provider>;
}
export function useMatch() { return useContext(MatchContext); }
